import { useEffect } from "react";
import { useTenant } from "@/contexts/TenantContext";

/**
 * Component that injects tenant branding colors into CSS variables
 * This ensures the entire app uses the tenant's brand colors
 */
export const BrandingInjector = () => {
  const { currentTenant } = useTenant();

  useEffect(() => {
    if (!currentTenant) return;

    const root = document.documentElement;

    // Helper to convert hex to HSL
    const hexToHSL = (hex: string): string => {
      // Remove # if present
      hex = hex.replace(/^#/, '');
      
      // Parse hex values
      const r = parseInt(hex.substring(0, 2), 16) / 255;
      const g = parseInt(hex.substring(2, 4), 16) / 255;
      const b = parseInt(hex.substring(4, 6), 16) / 255;

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      let h = 0, s = 0, l = (max + min) / 2;

      if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

        switch (max) {
          case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
          case g: h = ((b - r) / d + 2) / 6; break;
          case b: h = ((r - g) / d + 4) / 6; break;
        }
      }

      h = Math.round(h * 360);
      s = Math.round(s * 100);
      l = Math.round(l * 100);

      return `${h} ${s}% ${l}%`;
    };

    try {
      // Convert tenant colors to HSL and set CSS variables
      const primaryHSL = hexToHSL(currentTenant.primaryColor);
      const secondaryHSL = hexToHSL(currentTenant.secondaryColor);
      const backgroundHSL = hexToHSL(currentTenant.backgroundColor);

      console.log('🎨 Injecting tenant branding:', {
        name: currentTenant.name,
        primaryColor: currentTenant.primaryColor,
        primaryHSL,
        logo: currentTenant.logoUrl
      });

      // Set the primary brand color
      root.style.setProperty('--brand-gold', primaryHSL);
      root.style.setProperty('--primary', primaryHSL);
      root.style.setProperty('--accent', primaryHSL);
      root.style.setProperty('--ring', primaryHSL);

      // Set secondary color
      root.style.setProperty('--secondary-color', secondaryHSL);

      // Set background color
      root.style.setProperty('--bg-soft', backgroundHSL);
      root.style.setProperty('--background', backgroundHSL);

    } catch (error) {
      console.error('❌ Error injecting tenant branding:', error);
    }
  }, [currentTenant]);

  return null; // This component doesn't render anything
};
